/**
 * Code 128 barcodes, drawn as SVG, without a dependency.
 *
 * Every code the order picking system prints is Code 128 in code set B (printable ASCII): vak
 * codes (LOC-1A), order numbers (HVC-4821-903377-1204), command codes (CMD-LOCATION-EMPTY) and
 * badges. The Urovo RT40, like every rugged scanner, reads Code 128 out of the box.
 *
 * Why not a library: adding jsbarcode made npm rewrite a lockfile that had drifted from
 * package.json, adding ninety packages and removing five - noise in a change that should only
 * add a barcode. The encoding itself is small and fixed by ISO/IEC 15417; the bar table below is
 * the one jsbarcode ships (MIT licence), and code128.test.js checks this encoder against
 * jsbarcode's output for the codes this system prints.
 *
 * Code set B only. Set C would shorten long digit runs a little, but the order numbers have
 * dashes every few digits, so the saving is a few millimetres against a second code path.
 *
 * Pure apart from nothing: returns strings and numbers, the component renders them.
 */

/** Module patterns for symbol values 0..106 (1 = bar, 0 = space). 106 is the stop pattern. */
const PATTERNS = [
  "11011001100", "11001101100", "11001100110", "10010011000", "10010001100", "10001001100", "10011001000", "10011000100",
  "10001100100", "11001001000", "11001000100", "11000100100", "10110011100", "10011011100", "10011001110", "10111001100",
  "10011101100", "10011100110", "11001110010", "11001011100", "11001001110", "11011100100", "11001110100", "11101101110",
  "11101001100", "11100101100", "11100100110", "11101100100", "11100110100", "11100110010", "11011011000", "11011000110",
  "11000110110", "10100011000", "10001011000", "10001000110", "10110001000", "10001101000", "10001100010", "11010001000",
  "11000101000", "11000100010", "10110111000", "10110001110", "10001101110", "10111011000", "10111000110", "10001110110",
  "11101110110", "11010001110", "11000101110", "11011101000", "11011100010", "11011101110", "11101011000", "11101000110",
  "11100010110", "11101101000", "11101100010", "11100011010", "11101111010", "11001000010", "11110001010", "10100110000",
  "10100001100", "10010110000", "10010000110", "10000101100", "10000100110", "10110010000", "10110000100", "10011010000",
  "10011000010", "10000110100", "10000110010", "11000010010", "11001010000", "11110111010", "11000010100", "10001111010",
  "10100111100", "10010111100", "10010011110", "10111100100", "10011110100", "10011110010", "11110100100", "11110010100",
  "11110010010", "11011011110", "11011110110", "11110110110", "10101111000", "10100011110", "10001011110", "10111101000",
  "10111100010", "11110101000", "11110100010", "10111011110", "10111101110", "11101011110", "11110101110", "11010000100",
  "11010010000", "11010011100", "1100011101011",
];

const START_B = 104;
const STOP = 106;
const MODULO = 103;

/** Printable ASCII, the characters code set B can encode. */
export const isCode128BEncodable = (value) => typeof value === "string" && /^[\x20-\x7E]+$/.test(value);

/** The symbol values: start B, the data, the check symbol, stop. */
export const code128BSymbols = (value) => {
  if (!isCode128BEncodable(value)) {
    throw new Error(`Code 128B kan "${value}" niet coderen: alleen afdrukbare ASCII-tekens.`);
  }
  const data = [...value].map((character) => character.charCodeAt(0) - 32);
  const checksum = (START_B + data.reduce((sum, code, index) => sum + code * (index + 1), 0)) % MODULO;
  return [START_B, ...data, checksum, STOP];
};

/** The whole barcode as modules: "1101001000..." */
export const encodeCode128B = (value) =>
  code128BSymbols(value)
    .map((symbol) => PATTERNS[symbol])
    .join("");

/** The bars as runs of modules: [{ x, width }], x and width in modules. */
export const barsFromModules = (modules) => {
  const bars = [];
  let start = -1;
  for (let index = 0; index <= modules.length; index += 1) {
    const isBar = modules[index] === "1";
    if (isBar && start === -1) start = index;
    if (!isBar && start !== -1) {
      bars.push({ x: start, width: index - start });
      start = -1;
    }
  }
  return bars;
};

/**
 * Everything needed to draw one barcode. `quietZone` is in modules on either side; ten is the
 * minimum the standard asks for, scanners in a freezer do better with more.
 */
export const code128Layout = (value, { quietZone = 10 } = {}) => {
  const modules = encodeCode128B(value);
  return {
    value,
    modules,
    totalModules: modules.length + 2 * quietZone,
    quietZone,
    bars: barsFromModules(modules).map((bar) => ({ x: bar.x + quietZone, width: bar.width })),
  };
};
